// The Kora entry screen (packages/kora-kit/src/entry/EntryExperience.tsx), split into the pieces every account
// page shares. Styles, primitives and icons are the kit's own.
import { Eye, EyeOff } from "@kora/kit/icons";
import { Button } from "@kora/kit/primitives/Button/Button";
import "@kora/kit/entry/entry.css";
import "@kora/kit/entry/login-background.css";
import { useEffect, useId, useRef, type ReactNode } from "react";
import { copy } from "../lib/copy";
import type { OAuth2Client, UiText } from "../lib/kratos";
import "./portal.css";

export function Screen({ title, wide, children }: { title: string; wide?: boolean; children: ReactNode }) {
  const id = useId();
  const heading = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    heading.current?.focus();
  }, [title]);
  return (
    <main className="entry-login">
      <section className={`entry-login-panel${wide ? " entry-terms-panel" : ""}`} aria-labelledby={id}>
        <h1 id={id} ref={heading} tabIndex={-1} className="text-title">
          {title}
        </h1>
        {children}
      </section>
    </main>
  );
}

/** The entry flow's primary action: light button, dark text. */
export function PrimaryButton({ children, pending, disabled }: { children: ReactNode; pending?: boolean; disabled?: boolean }) {
  return (
    <Button variant="primary" size="lg" type="submit" loading={pending} disabled={disabled || pending} style={{ background: "var(--color-os-text)", color: "var(--color-os-bg)" }}>
      {children}
    </Button>
  );
}

export function GhostButton({ children, onClick, size, type = "button", disabled }: { children: ReactNode; onClick?: () => void; size?: "sm" | "md"; type?: "button" | "submit"; disabled?: boolean }) {
  return (
    <Button type={type} variant="ghost" size={size} onClick={onClick} disabled={disabled}>
      {children}
    </Button>
  );
}

export function PasswordToggle({ shown, onToggle }: { shown: boolean; onToggle: () => void }) {
  return (
    <Button type="button" variant="ghost" leadingIcon={shown ? <EyeOff /> : <Eye />} aria-pressed={shown} onClick={onToggle}>
      {shown ? "Hide password" : "Show password"}
    </Button>
  );
}

export function Messages({ messages }: { messages: UiText[] }) {
  return (
    <>
      {messages.map((m, i) =>
        m.type === "error" ? (
          <p key={`${m.id}-${i}`} role="alert" className="entry-error text-caption">
            {copy(m)}
          </p>
        ) : (
          <p key={`${m.id}-${i}`} role="status" className="text-caption entry-muted">
            {copy(m)}
          </p>
        ),
      )}
    </>
  );
}

export function Failure({ text }: { text?: string }) {
  return text ? (
    <p role="alert" className="entry-error text-caption">
      {text}
    </p>
  ) : null;
}

export function Note({ children }: { children: ReactNode }) {
  return <p className="text-caption entry-muted">{children}</p>;
}

const httpsOnly = (src: string) => {
  try {
    return new URL(src).protocol === "https:";
  } catch {
    return false;
  }
};

/** Which app an OAuth sign-in returns to, from the client's metadata. Images only; client scripts never run here. */
export function ClientNotice({ client }: { client?: OAuth2Client }) {
  if (!client) return null;
  const name = client.metadata?.name || client.client_name;
  const images = (client.metadata?.headerImages ?? []).filter(httpsOnly).slice(0, 3);
  if (!name && images.length === 0) return null;
  return (
    <div className="portal-client">
      {images.length > 0 && (
        <div className="portal-client-images">
          {images.map(src => (
            <img key={src} src={src} alt="" referrerPolicy="no-referrer" />
          ))}
        </div>
      )}
      {name && <p className="text-caption entry-muted">Continue to {name}</p>}
      {client.metadata?.subtitle && <p className="text-caption entry-muted">{client.metadata.subtitle}</p>}
    </div>
  );
}

export function Loading() {
  return (
    <main className="entry-login" aria-busy="true">
      <span className="portal-visually-hidden">Loading</span>
    </main>
  );
}
